import Papa from "papaparse";

/**
 * Represents a single data point with a date and numeric value.
 *
 * @example
 * const point: CsvDataPoint = { date: "2026-01-01", value: 100.5 };
 */
export type CsvDataPoint = {
  date: string;
  value: number;
};

/**
 * Result of strict CSV parsing - either success with data or error with details.
 */
export type CsvParseResult =
  | { success: true; data: CsvDataPoint[] }
  | { success: false; error: CsvParseError; lineNumber: number };

/**
 * Why a strict parse failed. Structured, not a sentence: the components turn it
 * into text in the user's language (`csvLineErrorMessage` in csv-parse-error.ts).
 */
export type CsvParseError =
  | { kind: "syntax"; detail: string }
  | { kind: "header"; got: string }
  | { kind: "missing_value" }
  | { kind: "invalid_date"; value: string }
  | { kind: "invalid_value"; value: string }
  | { kind: "not_increasing"; date: string; previous: string }
  | { kind: "no_data" };

/**
 * How the parser treats a possible header row.
 * - "required": first non-empty line MUST be `Date;Value` or `Date,Value` (case-insensitive).
 * - "optional": skip the first non-empty line if its first column isn't a date.
 * - "none":     parse every non-empty line as data (legacy behavior).
 */
export type CsvHeaderMode = "required" | "optional" | "none";

export type CsvParseOptions = {
  headerMode?: CsvHeaderMode;
};

/** Regex for yyyy-mm-dd format */
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
/** Regex for dd/mm/yyyy format */
const DMY_DATE_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;

function isDateValueHeader(parts: string[]): boolean {
  if (parts.length < 2) return false;
  return parts[0].trim().toLowerCase() === "date" && parts[1].trim().toLowerCase() === "value";
}

/**
 * Validates and normalizes a date string to yyyy-mm-dd format.
 * Accepts either yyyy-mm-dd or dd/mm/yyyy formats.
 *
 * @param dateStr - Date string to validate
 * @returns Normalized date in yyyy-mm-dd format, or null if invalid
 */
function parseAndNormalizeDate(dateStr: string): string | null {
  if (ISO_DATE_REGEX.test(dateStr)) {
    return dateStr;
  }
  if (DMY_DATE_REGEX.test(dateStr)) {
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month}-${day}`;
  }
  return null;
}

/**
 * Parses CSV with strict validation - returns error on any invalid line.
 * Use this when you want to stop parsing and show errors for invalid data.
 *
 * Format:
 * - Column delimiter may be `;` or `,` (auto-detected; `;` wins on ambiguity
 *   so European decimal-comma values like `2026-01-01;1,5` parse as 1.5).
 * - Numeric values may use `.` or `,` as the decimal separator; spaces are
 *   treated as thousands separators. Surrounding double quotes are stripped.
 * - Dates must be in strictly increasing order (oldest first).
 *
 * @param value - CSV string with each line as "date;value" or "date,value"
 * @param options.headerMode - How to treat a possible header row. Default: "optional".
 * @returns CsvParseResult with either parsed data or error details
 */
export function parseCsvToTimeSeriesStrict(
  value: string,
  options: CsvParseOptions = {},
): CsvParseResult {
  const headerMode: CsvHeaderMode = options.headerMode ?? "optional";
  const { data: rows, errors: parseErrors } = Papa.parse<string[]>(value, {
    delimitersToGuess: [";", ","],
    skipEmptyLines: false,
  });

  if (parseErrors.length > 0) {
    const first = parseErrors[0];
    return {
      success: false,
      error: { kind: "syntax", detail: first.message },
      lineNumber: (first.row ?? 0) + 1,
    };
  }

  const result: CsvDataPoint[] = [];
  let seenFirstNonEmptyLine = false;
  let previousDate: string | null = null;
  for (let i = 0; i < rows.length; i++) {
    const lineNumber = i + 1;
    const parts = rows[i].map((part) => part.trim()).filter((part) => part !== "");

    // Skip empty lines
    if (parts.length === 0) continue;

    const isFirstNonEmpty = !seenFirstNonEmptyLine;
    seenFirstNonEmptyLine = true;

    if (isFirstNonEmpty && headerMode === "required") {
      if (!isDateValueHeader(parts)) {
        return {
          success: false,
          error: { kind: "header", got: rows[i].join(", ") },
          lineNumber,
        };
      }
      continue;
    }

    if (parts.length < 2) {
      return {
        success: false,
        error: { kind: "missing_value" },
        lineNumber,
      };
    }

    const [dateStr, valueStr] = parts;
    const normalizedDate = parseAndNormalizeDate(dateStr);

    if (!normalizedDate && isFirstNonEmpty && headerMode === "optional") {
      // Treat the first line as a header row and skip it.
      continue;
    }

    if (!normalizedDate) {
      return {
        success: false,
        error: { kind: "invalid_date", value: dateStr },
        lineNumber,
      };
    }

    const cleanedValue = valueStr.replace(/\s/g, "").replace(",", ".");
    const numValue = cleanedValue === "" ? NaN : Number(cleanedValue);
    if (!Number.isFinite(numValue)) {
      return {
        success: false,
        error: { kind: "invalid_value", value: valueStr },
        lineNumber,
      };
    }

    if (previousDate !== null && normalizedDate <= previousDate) {
      return {
        success: false,
        error: { kind: "not_increasing", date: normalizedDate, previous: previousDate },
        lineNumber,
      };
    }
    previousDate = normalizedDate;

    result.push({ date: normalizedDate, value: numValue });
  }

  if (result.length === 0) {
    return {
      success: false,
      error: { kind: "no_data" },
      lineNumber: 1,
    };
  }

  return { success: true, data: result };
}

/**
 * Converts an array of CsvDataPoint objects back to a CSV string.
 *
 * @param data - Array of data points to convert
 * @returns CSV-formatted string with each line as "date;value"
 *
 * @example
 * ```ts
 * const data = [{ date: "2026-01-01", value: 100.5 }, { date: "2026-01-02", value: 101.2 }];
 * const csv = dataPointsToCsv(data);
 * // Returns: "2026-01-01;100.5\n2026-01-02;101.2"
 * ```
 */
export function dataPointsToCsv(data: CsvDataPoint[]): string {
  return data.map((item) => `${item.date};${item.value}`).join("\n");
}
